/* eslint-env jest */
import AbsolutePosImgs from './AbsolutePosImgs';
import { LAYOUTS } from './packageLayouts';
import collectImgs from '../../../testHelpers/collectImgs';

// AbsolutePosImgs is called as a plain function returning a React element tree,
// so we can invoke it directly and inspect the output without a DOM renderer.
// We mock the data hooks (and the asset imports) to feed it known poses and
// backgrounds, and stub Math.random so the "random vertical" pose picks are
// deterministic.

jest.mock('./Watermark_v2.png', () => 'watermark.png', { virtual: true });
jest.mock('./Horiz_Grad_Watermark.png', () => 'horiz-watermark.png', { virtual: true });
jest.mock('./AbsolutePosImgs.scss', () => ({}), { virtual: true });

let mockPosesData;
let mockBackgroundsData;
let mockPricelistData;
let mockOrientations; // url -> 'PORTRAIT' | 'LANDSCAPE' | null (null = still loading); default PORTRAIT
let mockPackagePosesData;
let mockPackagePosesPending;
let mockBackgroundsPending;

jest.mock('../../../hooks/usePoses', () => () => ({ data: mockPosesData }), { virtual: true });
jest.mock('../../../hooks/useMultiSeasonPoses', () => () => ({ data: mockPosesData }), { virtual: true });
jest.mock('../../../hooks/useBackgrounds', () => () => ({
  data: mockBackgroundsData,
  isPlaceholderData: mockBackgroundsPending,
}), { virtual: true });
jest.mock('../../../hooks/usePricelist', () => () => ({ data: mockPricelistData }), { virtual: true });
jest.mock('../../../hooks/usePackagePoses', () => () => ({
  data: mockPackagePosesData,
  isPlaceholderData: mockPackagePosesPending,
}), { virtual: true });
jest.mock('../../../hooks/useImageOrientations', () => (urls) => {
  const resolved = {};
  (urls || []).filter(Boolean).forEach((url) => {
    const orientation = mockOrientations[url] === undefined ? 'PORTRAIT' : mockOrientations[url];
    if (orientation) resolved[url] = { orientation };
  });
  return resolved;
}, { virtual: true });

const customer = { currentStudent: { id: 42 } };
const poses = (urls) => urls.map((imageUrl) => ({ imageUrl }));

beforeEach(() => {
  mockOrientations = {};
  mockPackagePosesData = [];
  mockPackagePosesPending = false;
  mockBackgroundsPending = false;
  mockPricelistData = { id: 1 };
  mockBackgroundsData = [
    { imageUrl: 'https://s3/Senior_Blue.png' },
    { imageUrl: 'https://s3/GrayBackdrop.png' },
    { imageUrl: 'https://s3/classroom.png' },
  ];
  mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-02.png', 'https://s3/p-03.png']);
});

describe('AbsolutePosImgs — render', () => {
  it('returns null when there are no poses', () => {
    mockPosesData = [];
    expect(AbsolutePosImgs(customer, 611, { name: 'Basic Package' })).toBeNull();
  });

  it('returns null when every pose is a horizontal .jpg', () => {
    mockPosesData = poses(['https://s3/p-01.jpg', 'https://s3/p-02.jpg']);
    expect(AbsolutePosImgs(customer, 611, { name: 'Basic Package' })).toBeNull();
  });

  it('returns null for an option with no preview layout', () => {
    expect(AbsolutePosImgs(customer, 611, { name: 'Magnets' })).toBeNull();
  });

  it('renders a pose + watermark per visible slot, plus a bg img where the slot has one', () => {
    // scholarsChoice has a background on every visible slot.
    const slots = LAYOUTS.scholarsChoice.filter(Boolean);
    const withBg = slots.filter((s) => s.background).length;
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const imgs = collectImgs(el);
    expect(imgs).toHaveLength(slots.length * 2 + withBg);
  });

  it('resolves pose1 to the first pose and a background tag to the school URL', () => {
    const randomSpy = jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    // scholarsChoice slot 1 is pose1 -> the student's first pose...
    expect(srcs).toContain('https://s3/p-01.png');
    // ...with the 'grayBg' tag, which resolves to the bg whose URL contains 'Gray'.
    expect(srcs).toContain('https://s3/GrayBackdrop.png');
    randomSpy.mockRestore();
  });

  it('uses the standard watermark for png poses', () => {
    jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const watermarks = collectImgs(el).filter((p) => p.alt === 'Watermark');
    expect(watermarks.length).toBeGreaterThan(0);
    watermarks.forEach((w) => expect(w.src).toBe('watermark.png'));
    Math.random.mockRestore();
  });

  it('falls back to the first background when no tag matches the school set', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/OnlyOne.png' }];
    jest.spyOn(Math, 'random').mockReturnValue(0);
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    const poseSrcs = new Set(mockPosesData.map((p) => p.imageUrl));
    // Every non-pose, non-watermark img (i.e. the backgrounds) falls back to OnlyOne.
    const bgSrcs = srcs.filter((s) => !poseSrcs.has(s) && s !== 'watermark.png');
    expect(bgSrcs.length).toBeGreaterThan(0);
    expect(new Set(bgSrcs)).toEqual(new Set(['https://s3/OnlyOne.png']));
    Math.random.mockRestore();
  });
});

describe('AbsolutePosImgs — pose role selection', () => {
  it('returns null while pose orientations are still resolving', () => {
    mockOrientations = { 'https://s3/p-01.png': null };
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
  });

  it('keeps landscape poses out of the vertical slots; a landscape only ever fills the 5:4 slot', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-08.png', 'https://s3/p-09.png']);
    mockOrientations = {
      'https://s3/p-08.png': 'LANDSCAPE',
      'https://s3/p-09.png': 'LANDSCAPE',
    };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const imgs = collectImgs(el);
    // portrait fills the vertical (4:5) print slots
    expect(imgs.some((i) => i.src === 'https://s3/p-01.png' && i.style.aspectRatio === '4 / 5')).toBe(true);
    // a landscape pose appears ONLY in the 5:4 horizontal slot, never a vertical (4:5) one
    imgs
      .filter((i) => i.src === 'https://s3/p-08.png' || i.src === 'https://s3/p-09.png')
      .forEach((i) => expect(i.style.aspectRatio).toBe('5 / 4'));
  });

  it('never uses graphic (G) renders, even portrait-oriented ones', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-G02.png']);
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/p-G02.png');
  });

  it('returns null while the pricelist Poses list is still loading', () => {
    mockPackagePosesPending = true;
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
  });

  it("draws the pool from the pricelist's Poses list, in admin order", () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '02', imageUrl: 'https://s3/p-02.png' },
      { number: '03', imageUrl: 'https://s3/p-03.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '02' }, { poseNumber: '03' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const poseSrcs = collectImgs(el)
      .map((p) => p.src)
      .filter((s) => s.startsWith('https://s3/p-'));
    // 01 is in the gallery but not on the pricelist; 02 leads as pose1.
    expect(poseSrcs).not.toContain('https://s3/p-01.png');
    expect(poseSrcs).toContain('https://s3/p-02.png');
  });

  it('fills the horizontal slot with a landscape pricelist pose (10-13 on pricelist 778)', () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '10', imageUrl: 'https://s3/p-10.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '01' }, { poseNumber: '10' }];
    mockOrientations = { 'https://s3/p-10.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const imgs = collectImgs(el);
    // portrait -> vertical (4:5) slots
    expect(imgs.some((i) => i.src === 'https://s3/p-01.png' && i.style.aspectRatio === '4 / 5')).toBe(true);
    // pose 10 (landscape) -> the 5:4 horizontal slot, never a vertical one
    const p10 = imgs.filter((i) => i.src === 'https://s3/p-10.png');
    expect(p10.length).toBeGreaterThan(0);
    p10.forEach((i) => expect(i.style.aspectRatio).toBe('5 / 4'));
  });

  it('never uses an OFF-pricelist landscape for the horizontal slot (whole-gallery leak)', () => {
    // Senior/multi-season case: the gallery (useMultiSeasonPoses) carries an off-pricelist
    // designer look 08 FIRST. It must NOT win the poseHoriz slot; the pricelist landscape 10 must.
    mockPosesData = [
      { number: '08', imageUrl: 'https://s3/p-08.png' }, // off-pricelist landscape, first in gallery
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '10', imageUrl: 'https://s3/p-10.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '01' }, { poseNumber: '10' }];
    mockOrientations = { 'https://s3/p-08.png': 'LANDSCAPE', 'https://s3/p-10.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const imgs = collectImgs(el);
    expect(imgs.map((i) => i.src)).not.toContain('https://s3/p-08.png'); // off-pricelist landscape never used
    const p10 = imgs.filter((i) => i.src === 'https://s3/p-10.png');
    expect(p10.length).toBeGreaterThan(0);
    p10.forEach((i) => expect(i.style.aspectRatio).toBe('5 / 4')); // the pricelist landscape fills the slot
  });

  it('matches pricelist rows by season (case-insensitive) on multi-season pricelists', () => {
    mockPricelistData = { id: 1, multipleSeasons: ['fall2026', 'fall2027'] };
    mockPosesData = [
      { number: '01', folder: 'fall2026', imageUrl: 'https://s3/old-01.png' },
      { number: '01', folder: 'fall2027', imageUrl: 'https://s3/new-01.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '01', season: 'Fall2027' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const poseSrcs = collectImgs(el)
      .map((p) => p.src)
      .filter((s) => s.includes('-01.png'));
    expect(poseSrcs).toContain('https://s3/new-01.png');
    expect(poseSrcs).not.toContain('https://s3/old-01.png');
  });

  it('falls back to the student portraits when no pricelist pose matches', () => {
    mockPosesData = [
      { number: '01', imageUrl: 'https://s3/p-01.png' },
      { number: '02', imageUrl: 'https://s3/p-02.png' },
    ];
    mockPackagePosesData = [{ poseNumber: '55' }];
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
  });

  it('renders nothing when no portrait-oriented pose exists (never the raw gallery)', () => {
    mockPosesData = poses(['https://s3/p-08.png', 'https://s3/p-G02.png']);
    mockOrientations = { 'https://s3/p-08.png': 'LANDSCAPE' };
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).toBeNull();
  });

  it('fails closed on UNKNOWN orientation (failed probe) poses', () => {
    mockPosesData = poses(['https://s3/p-01.png', 'https://s3/p-02.png']);
    mockOrientations = { 'https://s3/p-02.png': 'UNKNOWN' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/p-01.png');
    expect(srcs).not.toContain('https://s3/p-02.png');
  });

  it('does not wander to the last pose for U0 jobs with no 25.jpg class photo', () => {
    // Legacy poseHoriz scanning matched the JOB code (U0/J0/E0) and, finding no
    // 25.jpg, walked to the last pose — the framed-mockup G render.
    mockPosesData = poses(['https://s3/196-U001Q-x-01.png', 'https://s3/196-U001Q-x-G02.png']);
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).toContain('https://s3/196-U001Q-x-01.png');
    expect(srcs).not.toContain('https://s3/196-U001Q-x-G02.png');
  });

  it('fills the horizontal slot with the landscape frame, regardless of .png/.jpg extension', () => {
    // The pipeline now emits -25.png (once -25.jpg); either must fill the chalkboard slot.
    ['png', 'jpg'].forEach((ext) => {
      mockPosesData = poses(['https://s3/196-U001Q-x-01.png', `https://s3/196-U001Q-x-25.${ext}`]);
      mockOrientations = { [`https://s3/196-U001Q-x-25.${ext}`]: 'LANDSCAPE' };
      const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
      const srcs = collectImgs(el).map((p) => p.src);
      expect(srcs).toContain(`https://s3/196-U001Q-x-25.${ext}`); // fills the chalkboard slot
      expect(srcs).toContain('https://s3/196-U001Q-x-01.png'); // portraits still render
    });
  });
});

describe('AbsolutePosImgs — backgrounds and slot geometry', () => {
  it('never places a landscape (Horiz) background in a slot', () => {
    mockBackgroundsData = [
      { imageUrl: 'https://s3/Spring_Bridge_Horiz.png' },
      { imageUrl: 'https://s3/GrayBackdrop.png' },
    ];
    mockOrientations = { 'https://s3/Spring_Bridge_Horiz.png': 'LANDSCAPE' };
    jest.spyOn(Math, 'random').mockReturnValue(0);
    // scholarsChoice slots all carry background tags; none matches these names,
    // so the fallback background is used — it must be the portrait one.
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/Spring_Bridge_Horiz.png');
    expect(srcs).toContain('https://s3/GrayBackdrop.png');
    Math.random.mockRestore();
  });

  it('renders slots with no bg layer rather than a landscape-only background set', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/Spring_Bridge_Horiz.png' }];
    mockOrientations = { 'https://s3/Spring_Bridge_Horiz.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" });
    const srcs = collectImgs(el).map((p) => p.src);
    expect(srcs).not.toContain('https://s3/Spring_Bridge_Horiz.png');
    expect(srcs.some((s) => s.startsWith('https://s3/p-'))).toBe(true); // poses still render
  });

  it('waits for background orientations before rendering a bg-using layout', () => {
    mockBackgroundsData = [{ imageUrl: 'https://s3/GrayBackdrop.png' }];
    mockOrientations = { 'https://s3/GrayBackdrop.png': null };
    // scholarsChoice places backgrounds, so it must wait...
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
    // ...but picturePerfect has no background slots and renders regardless.
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).not.toBeNull();
  });

  it('waits for the backgrounds list itself only when the layout uses backgrounds', () => {
    mockBackgroundsPending = true;
    expect(AbsolutePosImgs(customer, 532, { name: "Scholar's Choice" })).toBeNull();
    expect(AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' })).not.toBeNull();
  });

  it('bounds each slot to the 4:5 / 5:4 aspect family; the landscape frame renders 5:4', () => {
    mockPosesData = poses(['https://s3/196-U001Q-x-01.png', 'https://s3/196-U001Q-x-25.png']);
    mockOrientations = { 'https://s3/196-U001Q-x-25.png': 'LANDSCAPE' };
    const el = AbsolutePosImgs(customer, 584, { name: 'Picture Perfect Package' });
    const imgs = collectImgs(el);
    expect(imgs.length).toBeGreaterThan(0);
    imgs.forEach((img) => {
      expect(['4 / 5', '5 / 4']).toContain(img.style.aspectRatio);
      expect(img.style.objectFit).toBe('contain');
    });
    // the landscape frame fills the horizontal slot, sized to its 5:4 aspect
    const landscape = imgs.filter((img) => img.src === 'https://s3/196-U001Q-x-25.png');
    expect(landscape.length).toBeGreaterThan(0);
    landscape.forEach((img) => expect(img.style.aspectRatio).toBe('5 / 4'));
    // portraits stay tall (4:5)
    const portrait = imgs.filter((img) => img.src === 'https://s3/196-U001Q-x-01.png');
    expect(portrait.length).toBeGreaterThan(0);
    portrait.forEach((img) => expect(img.style.aspectRatio).toBe('4 / 5'));
  });

  it("maps the 2026-27 'Single 8x10 Print' option name to the justAn8x10 layout", () => {
    const el = AbsolutePosImgs(customer, 584, { name: 'Single 8x10 Print' });
    expect(el).not.toBeNull();
    const poseImgs = collectImgs(el).filter((p) => p.src.startsWith('https://s3/p-'));
    expect(poseImgs.length).toBe(1); // justAn8x10 renders exactly one pose slot
  });
});

describe('AbsolutePosImgs — named buyAllTile layout', () => {
  it('renders both prints as bare cutouts on their measured blue areas (no bg layer, no frame)', () => {
    const el = AbsolutePosImgs(customer, 877, { name: 'All Digital Package' }, { layoutKey: 'buyAllTile' });
    const imgs = collectImgs(el);
    // pose + watermark per slot, and nothing else -- a background img here
    // would cover the art's baked YOU SAVE roundel.
    expect(imgs).toHaveLength(4);
    expect(imgs.some((img) => img.style.aspectRatio === '194 / 246' && img.style.top === '38.6%')).toBe(true);
    expect(imgs.some((img) => img.style.aspectRatio === '184 / 246' && img.style.left === '49.1%')).toBe(true);
  });
});
